import {
  DateRadioChipGroup,
  FormField,
  LoadingDots,
  Paragraph,
  RadioChipGroup,
  RadioChipOptions,
  Select,
} from '@expressable/syntax-ds';
import { format } from 'date-fns';
import { useConsultationCalendar } from 'domains/calendars/v3/useConsultationCalendar';
import { TimezoneOption, timeZoneOptions } from 'domains/timezone';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { isEmpty, isNumber, isUndefined } from 'lodash';
import { FC, useState } from 'react';
import { useController, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PageSizeConfig } from './CalendarPageConfig';

type Props = {
  onChange?: (timeSlot?: Date) => void;
  calendarData: ReturnType<typeof useConsultationCalendar>;
  name: string;
};

// Experimental component, currently only meant to be used in single page signup. Will be rewritten and ported to syntax

export const ConsultationCalendarPicker: FC<Props> = ({ calendarData, onChange, name }) => {
  const [selectedDayIndex, setSelectedDayIndex] = useState<number>();
  const { field } = useController({
    name,
  });

  const { isSubmitting } = useFormState();
  const { currentTimeSlots, dayIndex, isLoading, onChangeDay, data } = calendarData;

  const { displayTimezone, setDisplayTimezone, format: formatInTimezone } = useDisplayTimezone();
  const dayOptions: RadioChipOptions | undefined = data?.map(
    (d, index) =>
      ({
        value: `${index}`,
        date: d.day,
      } as RadioChipOptions[number]),
  );

  const onChangeTimeSlot = (timeSlotIndex?: number) => {
    setSelectedDayIndex(timeSlotIndex);

    const timeSlot = isNumber(timeSlotIndex) ? currentTimeSlots?.[timeSlotIndex] : undefined;

    onChange?.(timeSlot);
    field.onChange(timeSlot);
  };

  const { t } = useTranslation();

  const noTimeSlotsForCurrentDay = !currentTimeSlots?.length;

  const GhostAnchor = () => <div ref={field.ref} />;

  return (
    <>
      <FormField label={t('directSignup.schedule.timezone.label')} name={!displayTimezone ? name : undefined}>
        <Select
          autoFocus
          options={timeZoneOptions}
          onChange={selectedValue => {
            if (isEmpty(selectedValue)) {
              onChangeTimeSlot(undefined);
              setDisplayTimezone(undefined);
            } else {
              setDisplayTimezone(timeZoneOptions.find(({ value }) => value === selectedValue) as TimezoneOption);
            }
          }}
          value={displayTimezone?.value}
          disabled={(!isUndefined(displayTimezone) && isLoading) || isSubmitting}
          standalone
        />
      </FormField>

      {(isLoading || !dayOptions) && (
        <LoadingDots className="flex flex-col h-max static" message={`Loading available times`} />
      )}

      {!displayTimezone && (
        <Paragraph>
          {t('directSignup.schedule.noTimezone')} <GhostAnchor />
        </Paragraph>
      )}

      {displayTimezone && dayOptions && (
        <>
          <DateRadioChipGroup
            htmlName="daypicker"
            label={t('directSignup.schedule.dayPicker.label')}
            chips={dayOptions}
            variant="carousel"
            value={`${dayIndex}`}
            pageSize={PageSizeConfig}
            onChange={e => {
              onChangeTimeSlot(undefined);
              onChangeDay(parseInt(e.target.value, 10));
            }}
            disabled={isLoading || isSubmitting}
            name={noTimeSlotsForCurrentDay || isUndefined(dayIndex) ? name : undefined}
            dateFormatFn={format}
          />

          {noTimeSlotsForCurrentDay && (
            <Paragraph>
              {t('directSignup.schedule.noSlots')}
              <GhostAnchor />
            </Paragraph>
          )}

          {currentTimeSlots && !noTimeSlotsForCurrentDay && (
            <>
              <RadioChipGroup
                htmlName="timeslot"
                label={t('directSignup.sections.consultation.calendarLabel')}
                hint={t('directSignup.sections.consultation.calendarHint')}
                pageSize={PageSizeConfig}
                className="mobile-xs-d:grid-cols-3"
                chips={currentTimeSlots.map((slot, index) => ({
                  value: `${index}`,
                  label: formatInTimezone(slot, 'h:mm a'),
                }))}
                variant="standard"
                onChange={e => {
                  const pickedIndex = parseInt(e.target.value);

                  onChangeTimeSlot(pickedIndex);
                }}
                disabled={isSubmitting}
                name={name}
                value={selectedDayIndex}
              />
              <GhostAnchor />
            </>
          )}
        </>
      )}
    </>
  );
};
